import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import plusCircleIcon from '../../assets/icons/plus-circle.svg';
import searchIcon from '../../assets/icons/search.svg';
import xIcon from '../../assets/icons/x.svg';

const BLUE_FILTER = 'brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1234%) hue-rotate(194deg) brightness(95%) contrast(101%)';
const GREY_FILTER = 'brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)';

const CaisseSelector = ({ caisses = [], selectedIds = [], onChange, disabled = false, hideTabs = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const chipsContainerRef = useRef(null);

  const physiqueCaisses = caisses.filter(c => c.type === 'PHYSIQUE');
  const mobileCaisses = caisses.filter(c => c.type === 'MOBILE');

  const getSelectionType = () => {
    if (selectedIds.length === 0) return null;
    return caisses.find(c => selectedIds.includes(c.id))?.type || null;
  };

  const getInitialTab = () => {
    const selType = getSelectionType();
    if (selType) return selType;
    return physiqueCaisses.length > 0 ? 'PHYSIQUE' : 'MOBILE';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const selectedIdsKey = selectedIds.join(',');

  useEffect(() => {
    const selType = getSelectionType();
    if (selType) setActiveTab(selType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdsKey]);

  // Collapse when selection changes
  useEffect(() => {
    setExpanded(false);
  }, [selectedIdsKey]);

  const measure = () => {
    const container = chipsContainerRef.current;
    if (!container) return;
    const children = Array.from(container.children);
    if (children.length === 0) { setHiddenCount(0); return; }
    const firstRowTop = children[0].offsetTop;
    const hidden = children.filter(child => child.offsetTop > firstRowTop + 4).length;
    setHiddenCount(hidden);
  };

  useLayoutEffect(() => {
    if (expanded || selectedIds.length === 0) { setHiddenCount(0); return; }
    measure();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdsKey, expanded]);

  useEffect(() => {
    const el = chipsContainerRef.current;
    if (!el || expanded) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded, selectedIdsKey]);

  const selectedType = getSelectionType();
  const tabCaisses = activeTab === 'PHYSIQUE' ? physiqueCaisses : mobileCaisses;
  const filteredCaisses = tabCaisses.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const allPhysiqueSelected = physiqueCaisses.length > 0 && physiqueCaisses.every(c => selectedIds.includes(c.id));
  const allMobileSelected = mobileCaisses.length > 0 && mobileCaisses.every(c => selectedIds.includes(c.id));
  const allCurrentTabSelected = activeTab === 'PHYSIQUE' ? allPhysiqueSelected : allMobileSelected;

  const close = () => {
    setIsOpen(false);
    setDropdownPosition(null);
    setSearch('');
  };

  const handleToggle = () => {
    if (disabled) return;
    if (isOpen) {
      close();
    } else {
      setIsOpen(true);
    }
  };

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom + 8, left: rect.left });
    }
  }, [isOpen]);

  useEffect(() => {
    if (dropdownPosition && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [dropdownPosition]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        close();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleReplace = (id) => {
    onChange([id]);
    close();
  };

  const handleAdd = (e, caisse) => {
    e.stopPropagation();
    if (selectedIds.includes(caisse.id)) return;
    if (selectedType !== null && selectedType !== caisse.type) {
      onChange([caisse.id]);
    } else {
      onChange([...selectedIds, caisse.id]);
    }
    close();
  };

  const handleRemove = (e, id) => {
    if (e) e.stopPropagation();
    onChange(selectedIds.filter(sid => sid !== id));
    if (isOpen) close();
  };

  const handleSelectAll = (type) => {
    const ids = (type === 'PHYSIQUE' ? physiqueCaisses : mobileCaisses).map(c => c.id);
    onChange(ids);
    close();
  };

  const BADGE_STYLE = {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    border: '1px solid #027BC6',
    borderRadius: '19px',
    padding: '3px 10px',
    backgroundColor: '#027BC614',
    cursor: 'pointer',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    fontFamily: 'Open Sans',
    fontSize: '13px',
    fontWeight: 600,
    color: '#027BC6',
    userSelect: 'none',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={handleToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          cursor: disabled ? 'default' : 'pointer',
          flexShrink: 0,
          alignSelf: 'flex-start',
          paddingTop: '4px',
        }}
      >
        <span style={{
          fontFamily: 'Montserrat',
          fontWeight: 600,
          fontSize: '18px',
          lineHeight: '24px',
          color: '#027BC6',
        }}>
          {selectedIds.length === 0
            ? 'Sélectionnez une caisse'
            : `${selectedIds.length} ${selectedIds.length === 1 ? 'caisse' : 'caisses'}`}
        </span>
        {!disabled && (
          <img
            src={chevronDownIcon}
            alt=""
            style={{
              width: '12px',
              height: '12px',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              filter: BLUE_FILTER,
            }}
          />
        )}
      </div>

      {/* Chips container */}
      {selectedIds.length > 0 && (
        <div
          ref={chipsContainerRef}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            overflow: expanded ? 'visible' : 'hidden',
            maxHeight: expanded ? 'none' : '32px',
          }}
        >
          {selectedIds.map(id => {
            const caisse = caisses.find(c => c.id === id);
            if (!caisse) return null;
            return (
              <div
                key={id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  border: '1px solid #027BC6',
                  borderRadius: '19px',
                  padding: '3px 8px 3px 12px',
                  backgroundColor: '#027BC614',
                  flexShrink: 0,
                }}
              >
                <span style={{
                  fontFamily: 'Open Sans',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#027BC6',
                  whiteSpace: 'nowrap',
                }}>
                  {caisse.name}
                </span>
                {!disabled && (
                  <button
                    onClick={(e) => handleRemove(e, id)}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      padding: '0 2px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <img src={xIcon} alt="Retirer" style={{ width: '14px', height: '14px', filter: BLUE_FILTER }} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* +N expand badge */}
      {!expanded && hiddenCount > 0 && (
        <div onClick={() => setExpanded(true)} style={{ ...BADGE_STYLE, alignSelf: 'flex-start', marginTop: '2px' }}>
          +{hiddenCount}
        </div>
      )}

      {/* Collapse badge */}
      {expanded && (
        <div onClick={() => setExpanded(false)} style={{ ...BADGE_STYLE, alignSelf: 'flex-start', marginTop: '2px' }}>
          <img src={chevronUpIcon} alt="" style={{ width: '11px', height: '11px', filter: BLUE_FILTER }} />
        </div>
      )}

      {/* Dropdown portal */}
      {isOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: 1500,
            background: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.12)',
            width: '320px',
            maxHeight: '440px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {!hideTabs && (
            <div style={{ padding: '10px 10px 8px', backgroundColor: 'white', borderRadius: '12px 12px 0 0', flexShrink: 0 }}>
              <div style={{ display: 'flex', backgroundColor: '#F7F7F7', border: '1px solid #E9EBED', borderRadius: '12px', height: '40px', overflow: 'hidden' }}>
                {physiqueCaisses.length > 0 && (
                  <button
                    onClick={() => { setActiveTab('PHYSIQUE'); setSearch(''); searchInputRef.current?.focus(); }}
                    style={{
                      flex: 1, border: 'none', borderRadius: '10px', cursor: 'pointer',
                      fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600,
                      backgroundColor: activeTab === 'PHYSIQUE' ? '#027BC6' : 'transparent',
                      color: activeTab === 'PHYSIQUE' ? 'white' : '#9A9A9A',
                      transition: 'background-color 0.15s', whiteSpace: 'nowrap',
                    }}
                  >
                    Caisses physiques
                  </button>
                )}
                {mobileCaisses.length > 0 && (
                  <button
                    onClick={() => { setActiveTab('MOBILE'); setSearch(''); searchInputRef.current?.focus(); }}
                    style={{
                      flex: 1, border: 'none', borderRadius: '10px', cursor: 'pointer',
                      fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600,
                      backgroundColor: activeTab === 'MOBILE' ? '#027BC6' : 'transparent',
                      color: activeTab === 'MOBILE' ? 'white' : '#9A9A9A',
                      transition: 'background-color 0.15s', whiteSpace: 'nowrap',
                    }}
                  >
                    Caisses mobiles
                  </button>
                )}
              </div>
            </div>
          )}

          <div style={{ padding: `${hideTabs ? '10px' : '0'} 10px 8px`, flexShrink: 0 }}>
            <div style={{ position: 'relative' }}>
              <img src={searchIcon} alt="" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', pointerEvents: 'none', filter: GREY_FILTER }} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Recherche..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && filteredCaisses.length === 1) handleReplace(filteredCaisses[0].id); }}
                style={{
                  width: '100%', height: '40px', padding: '0 14px 0 40px',
                  border: '1px solid #E9EBED', borderRadius: '12px',
                  fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600,
                  outline: 'none', boxSizing: 'border-box',
                  backgroundColor: '#F7F7F7', color: '#AFB0B2',
                }}
              />
            </div>
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {!search && (
              <div
                onClick={() => handleSelectAll(activeTab)}
                style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', margin: '2px 6px', cursor: 'pointer', borderRadius: '8px', backgroundColor: allCurrentTabSelected ? '#EBF5FC' : 'transparent' }}
                onMouseEnter={e => { if (!allCurrentTabSelected) e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = allCurrentTabSelected ? '#EBF5FC' : 'transparent'; }}
              >
                <span style={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 700, color: '#027BC6' }}>
                  Toutes les caisses {activeTab === 'PHYSIQUE' ? 'physiques' : 'mobiles'}
                </span>
              </div>
            )}

            {filteredCaisses.map(caisse => {
              const isSelected = selectedIds.includes(caisse.id);
              return (
                <div
                  key={caisse.id}
                  onClick={() => handleReplace(caisse.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', margin: '2px 6px', cursor: 'pointer', backgroundColor: isSelected ? '#EBF5FC' : 'transparent', borderRadius: '8px' }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = isSelected ? '#EBF5FC' : 'transparent'; }}
                >
                  <button
                    onClick={(e) => isSelected ? handleRemove(e, caisse.id) : handleAdd(e, caisse)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, display: 'flex', flexShrink: 0 }}
                  >
                    {isSelected ? (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="10" fill="#027BC6" />
                        <line x1="5" y1="10" x2="15" y2="10" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
                      </svg>
                    ) : (
                      <img src={plusCircleIcon} alt="Ajouter" style={{ width: '20px', height: '20px', filter: BLUE_FILTER }} />
                    )}
                  </button>
                  <span style={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#38485C', flex: 1, wordBreak: 'break-word' }}>
                    {caisse.name}
                  </span>
                </div>
              );
            })}

            {filteredCaisses.length === 0 && (
              <div style={{ padding: '20px 16px', textAlign: 'center', fontFamily: 'Montserrat', fontSize: '13px', color: '#9A9A9A' }}>
                Aucune caisse trouvée
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CaisseSelector;
